import {
  CheckOutlined,
  ClearOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  Divider,
  Input,
  Modal,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ColumnProps } from "antd/lib/table";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AppContext from "../../../context/app/context";
import FieldBuilder from "../../../components/form-components/field-builder";
import Form from "../../../components/form-components/from";
import PageHeader from "../../../components/general/layout/page-header";
import SearchFilterCard from "../../../components/general/filter-card";
import RefreshBtn from "../../../components/general/table-components/actions/refresh-btn";
import Table from "../../../components/general/table-components/table";
import {
  BatchWriteOffSource,
  BatchWriteOffStatus,
  getBatchWriteOffReasonLabel,
  getBatchWriteOffReasonOptions,
  getBatchWriteOffSourceColor,
  getBatchWriteOffSourceLabel,
  getBatchWriteOffStatusColor,
  getBatchWriteOffStatusLabel,
} from "../../../models/wms/batch-write-off/enum";
import { IBatchWriteOffQuery } from "../../../models/wms/batch-write-off/query";
import { IBatchWriteOff } from "../../../models/wms/batch-write-off/response";
import { normalizeBatchWriteOffListingResponse } from "../../../models/wms/batch-write-off/mapper";
import EndPoints from "../../../services/end-points";
import IBaseListingResponse from "../../../utils/api/base-listing-response";
import { Permissions } from "../../../utils/Permissions/Permissions";
import styles from "./style.module.scss";

const buildEmptyQuery = (perPage = 15): IBatchWriteOffQuery => ({
  page: 1,
  perPage,
  status: undefined,
  source: undefined,
  reason: undefined,
  orderNumber: undefined,
  productId: undefined,
  storeId: undefined,
});

const toNumberOrUndefined = (value: any) => {
  if (value === undefined || value === null || value === "") return undefined;
  return Number(value);
};

const formatUserName = (user?: any) => {
  if (!user) return "-";
  return (
    user.fullName ||
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
    `#${user.id}`
  );
};

const getLocalizedName = (
  name: string | { ar?: string | null; en?: string | null } | null | undefined,
  language: string,
) => {
  if (!name) return "-";
  if (typeof name === "string") return name;
  return language.startsWith("ar")
    ? name.ar || name.en || "-"
    : name.en || name.ar || "-";
};

type ActionType = "confirm" | "reverse";

const isReasonAllowedForSource = (reason?: number, source?: number | null) => {
  if (reason === undefined) return true;
  return getBatchWriteOffReasonOptions(source).includes(reason);
};

const isSystemGeneratedNote = (note?: string | null) => {
  const normalizedNote = note?.trim();

  if (!normalizedNote) return false;

  return (
    normalizedNote.startsWith(
      "System-created pending write-off from order fulfillment shortage",
    ) ||
    (normalizedNote.includes("order_id=") &&
      normalizedNote.includes("product_order_id=") &&
      normalizedNote.includes("initiated_by="))
  );
};

interface IBatchWriteOffFilterFieldsProps {
  products: { label: string; value: number }[];
  stores: { label: string; value: number }[];
  loading: string[];
  getProducts: (keyword?: string) => void;
}

const BatchWriteOffFilterFields: React.FC<IBatchWriteOffFilterFieldsProps> = ({
  products,
  stores,
  loading,
  getProducts,
}) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } =
    useFormContext<IBatchWriteOffQuery>();
  const source = toNumberOrUndefined(useWatch({ control, name: "source" }));
  const reasonOptions = getBatchWriteOffReasonOptions(source).map((reason) => ({
    label: t(getBatchWriteOffReasonLabel(reason)),
    value: reason,
  }));

  useEffect(() => {
    const reason = toNumberOrUndefined(getValues("reason"));

    if (!isReasonAllowedForSource(reason, source)) {
      setValue("reason", undefined);
    }
  }, [source, getValues, setValue]);

  return (
    <Row gutter={[10, 0]}>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="status"
          label={t("status")}
          width="large"
          input={{
            type: "select",
            options: [
              {
                label: t("batch_write_off_status_pending_confirmation"),
                value: BatchWriteOffStatus.PendingConfirmation,
              },
              {
                label: t("batch_write_off_status_confirmed"),
                value: BatchWriteOffStatus.Confirmed,
              },
              {
                label: t("batch_write_off_status_reversed"),
                value: BatchWriteOffStatus.Reversed,
              },
            ],
          }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="source"
          label={t("source")}
          width="large"
          input={{
            type: "select",
            options: [
              {
                label: t(
                  getBatchWriteOffSourceLabel(BatchWriteOffSource.Manual),
                ),
                value: BatchWriteOffSource.Manual,
              },
              {
                label: t(
                  getBatchWriteOffSourceLabel(
                    BatchWriteOffSource.OrderFulfillmentShortage,
                  ),
                ),
                value: BatchWriteOffSource.OrderFulfillmentShortage,
              },
            ],
          }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="reason"
          label={t("reason")}
          width="large"
          input={{
            type: "select",
            options: reasonOptions,
          }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="orderNumber"
          label={t("order_number")}
          width="large"
          input={{ type: "text" }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="productId"
          label={t("select_product")}
          width="large"
          input={{
            type: "select-pagination",
            allowSearch: true,
            loading: loading.includes("products"),
            handleSearch: getProducts,
            options: products,
          }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="storeId"
          label={t("select_store")}
          width="large"
          input={{
            type: "select",
            allowSearch: true,
            loading: loading.includes("stores"),
            options: stores,
          }}
        />
      </Col>
    </Row>
  );
};

const BatchWriteOffPage = () => {
  const { t, i18n } = useTranslation();
  const { direction } = useContext(AppContext);
  const [query, setQuery] = useState<IBatchWriteOffQuery>(buildEmptyQuery());
  const [list, setList] = useState<IBaseListingResponse<IBatchWriteOff>>();
  const [loading, setLoading] = useState<string[]>([]);
  const [stores, setStores] = useState<{ label: string; value: number }[]>([]);
  const [products, setProducts] = useState<{ label: string; value: number }[]>(
    [],
  );
  const [actionModal, setActionModal] = useState<{
    type: ActionType;
    record: IBatchWriteOff;
  }>();
  const [actionNotes, setActionNotes] = useState("");
  const userPermissions =
    localStorage.getItem("permisionStoroge")?.split(",") || [];
  const canWriteOff = userPermissions.includes(
    Permissions.CAN_WRITE_OFF_WMS_BATCH,
  );

  const setLoadingKey = (key: string, value: boolean) => {
    setLoading((current) =>
      value
        ? current.includes(key)
          ? current
          : [...current, key]
        : current.filter((item) => item !== key),
    );
  };

  const getData = async () => {
    try {
      setLoadingKey("list", true);
      const { data } = await EndPoints.wmsBatchWriteOff.getAll(query);
      setList(normalizeBatchWriteOffListingResponse(data));
    } catch {
      setList({
        data: [],
        total: 0,
        currentPage: query.page ?? 1,
        lastPage: 1,
      });
      message.error(t("batch_write_offs_load_failed"));
    } finally {
      setLoadingKey("list", false);
    }
  };

  const getStores = async () => {
    try {
      setLoadingKey("stores", true);
      const { data } = await EndPoints.wmsShared.getDropdownStores();
      setStores(
        (data?.data ?? []).map((store: any) => ({
          label: getLocalizedName(store.name, i18n.language),
          value: store.id,
        })),
      );
    } catch {
      setStores([]);
    } finally {
      setLoadingKey("stores", false);
    }
  };

  const getProducts = async (keyword?: string) => {
    try {
      setLoadingKey("products", true);
      const { data } = await EndPoints.product.getAllProducts({
        light: true,
        page: 1,
        perPage: 50,
        keyword: keyword || undefined,
      });
      setProducts(
        (data?.data ?? []).map((product: any) => ({
          label: product.barcode
            ? `${product.name} - ${product.barcode}`
            : product.name,
          value: product.id,
        })),
      );
    } catch {
      setProducts([]);
    } finally {
      setLoadingKey("products", false);
    }
  };

  useEffect(() => {
    getStores();
    getProducts();
  }, [direction, i18n.language]);

  useEffect(() => {
    getData();
  }, [query, direction]);

  const resetFilters = () => {
    setQuery(buildEmptyQuery(query.perPage));

    const form = document.getElementById("batch-write-off-filter");
    if (form) {
      form.dispatchEvent(
        new Event("reset", { cancelable: true, bubbles: true }),
      );
    }
  };

  const submitFilters = (data: any) => {
    const source = toNumberOrUndefined(data.source);
    const reason = toNumberOrUndefined(data.reason);

    setQuery({
      page: 1,
      perPage: query.perPage,
      status: toNumberOrUndefined(data.status),
      source,
      reason: isReasonAllowedForSource(reason, source) ? reason : undefined,
      orderNumber: data.orderNumber?.trim() || undefined,
      productId: toNumberOrUndefined(data.productId),
      storeId: toNumberOrUndefined(data.storeId),
    });
  };

  const openActionModal = (type: ActionType, record: IBatchWriteOff) => {
    setActionModal({ type, record });
    setActionNotes("");
  };

  const submitAction = async () => {
    if (!actionModal) return;

    const loadingKey = `${actionModal.type}-${actionModal.record.id}`;
    const payload = {
      notes: actionNotes.trim() || undefined,
    };

    try {
      setLoadingKey(loadingKey, true);
      if (actionModal.type === "confirm") {
        await EndPoints.wmsBatchWriteOff.confirm(
          actionModal.record.id,
          payload,
        );
        message.success(t("batch_write_off_confirmed_successfully"));
      } else {
        await EndPoints.wmsBatchWriteOff.reverse(
          actionModal.record.id,
          payload,
        );
        message.success(t("batch_write_off_reversed_successfully"));
      }
      setActionModal(undefined);
      setActionNotes("");
      await getData();
    } catch {
      message.error(t("batch_write_off_action_failed"));
    } finally {
      setLoadingKey(loadingKey, false);
    }
  };

  const renderStatusTag = (status?: number | null) => (
    <Tag color={getBatchWriteOffStatusColor(status)}>
      {t(getBatchWriteOffStatusLabel(status))}
    </Tag>
  );

  const renderSourceTag = (source?: number | null) => (
    <Tag color={getBatchWriteOffSourceColor(source)}>
      {t(getBatchWriteOffSourceLabel(source))}
    </Tag>
  );

  const renderReason = (reason?: IBatchWriteOff["reason"]) => {
    const labelKey = getBatchWriteOffReasonLabel(reason?.value);

    if (labelKey !== "not_determined") return t(labelKey);
    return reason?.name ?? t("not_determined");
  };

  const renderNotes = (record: IBatchWriteOff) => {
    const notes = [
      record.notes && !isSystemGeneratedNote(record.notes)
        ? { label: t("write_off_note"), value: record.notes }
        : undefined,
      record.confirmationNotes
        ? { label: t("confirmation_note"), value: record.confirmationNotes }
        : undefined,
      record.reversalNotes
        ? { label: t("quantity_restore_note"), value: record.reversalNotes }
        : undefined,
    ].filter((note): note is { label: string; value: string } =>
      Boolean(note?.value),
    );

    if (!notes.length) return "-";

    const fullNotes =
      notes.length === 1
        ? notes[0].value
        : notes.map((note) => `${note?.label}: ${note?.value}`).join("\n");

    return (
      <Typography.Paragraph
        style={{ marginBottom: 0, whiteSpace: "pre-line" }}
        ellipsis={{ rows: 3, tooltip: fullNotes }}
      >
        {fullNotes}
      </Typography.Paragraph>
    );
  };

  const renderDates = (record: IBatchWriteOff) => (
    <Space direction="vertical" size={0}>
      <Typography.Text>
        {record.createdAt
          ? moment(record.createdAt).format("YYYY-MM-DD HH:mm:ss")
          : "-"}
      </Typography.Text>
      {record.confirmedAt && (
        <Typography.Text type="secondary">
          {t("confirmed_at")}:{" "}
          {moment(record.confirmedAt).format("YYYY-MM-DD HH:mm:ss")}
        </Typography.Text>
      )}
      {record.reversedAt && (
        <Typography.Text type="secondary">
          {t("reversed_at")}:{" "}
          {moment(record.reversedAt).format("YYYY-MM-DD HH:mm:ss")}
        </Typography.Text>
      )}
    </Space>
  );

  const columns: ColumnProps<IBatchWriteOff>[] = [
    {
      title: t("write_off_id"),
      dataIndex: "id",
      key: "id",
      width: 100,
      align: "center",
    },
    {
      title: t("order_number"),
      key: "orderNumber",
      width: 130,
      align: "center",
      render: (_, record) =>
        record.order?.number ? (
          <Link to={`/orders/details/${record.order.id}`}>
            {record.order.number}
          </Link>
        ) : (
          "-"
        ),
    },
    {
      title: t("product"),
      key: "product",
      width: 230,
      render: (_, record) => (
        <>
          {record.product?.id ? (
            <Link to={`/products/details/${record.product.id}`}>
              {getLocalizedName(record.product.name, i18n.language)}
            </Link>
          ) : (
            getLocalizedName(record.product?.name, i18n.language)
          )}
        </>
      ),
    },
    {
      title: t("barcode"),
      key: "barcode",
      width: 150,
      render: (_, record) => record.product?.barcode ?? "-",
    },
    {
      title: t("store"),
      key: "store",
      width: 220,
      render: (_, record) => (
        <>
          {record.store?.id ? (
            <Link to={`/stores/details/${record.store.id}`}>
              {getLocalizedName(record.store.name, i18n.language)}
            </Link>
          ) : (
            getLocalizedName(record.store?.name, i18n.language)
          )}
        </>
      ),
    },
    {
      title: t("batch_number"),
      key: "batch",
      width: 150,
      render: (_, record) => record.batch?.batchNumber ?? "-",
    },
    {
      title: t("expiration_date"),
      key: "expirationDate",
      width: 150,
      align: "center",
      render: (_, record) =>
        record.batch?.expirationDate
          ? moment(record.batch.expirationDate).format("YYYY-MM-DD")
          : "-",
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "center",
      render: (quantity) => (
        <Typography.Text strong>{quantity}</Typography.Text>
      ),
    },
    {
      title: t("reason"),
      dataIndex: "reason",
      key: "reason",
      width: 180,
      render: renderReason,
    },
    {
      title: t("source"),
      dataIndex: "source",
      key: "source",
      width: 190,
      align: "center",
      render: (source) => renderSourceTag(source?.value),
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      width: 150,
      align: "center",
      render: (status) => renderStatusTag(status?.value),
    },
    {
      title: t("write_off_initiated_by"),
      dataIndex: "initiatedBy",
      key: "initiatedBy",
      width: 170,
      render: formatUserName,
    },
    {
      title: t("manual_write_off_creator"),
      dataIndex: "createdBy",
      key: "createdBy",
      width: 170,
      render: formatUserName,
    },
    {
      title: t("confirmed_by"),
      dataIndex: "confirmedBy",
      key: "confirmedBy",
      width: 170,
      render: formatUserName,
    },
    {
      title: t("reversed_by"),
      dataIndex: "reversedBy",
      key: "reversedBy",
      width: 170,
      render: formatUserName,
    },
    {
      title: t("write_off_dates"),
      key: "writeOffDates",
      width: 240,
      render: (_, record) => renderDates(record),
    },
    {
      title: t("write_off_notes"),
      key: "writeOffNotes",
      width: 310,
      render: (_, record) => renderNotes(record),
    },
    {
      title: t("actions"),
      key: "actions",
      fixed: "right",
      width: 96,
      align: "center",
      render: (_, record) => {
        const hasActions = record.canConfirm || record.canReverse;

        if (!hasActions) return "-";

        return (
          <Space size={4} className={styles.actionButtons}>
            {record.canConfirm && (
              <Tooltip
                title={
                  canWriteOff
                    ? t("confirm_batch_write_off_action")
                    : t("not_allowed")
                }
              >
                <span>
                  <Button
                    type="primary"
                    size="small"
                    shape="circle"
                    icon={<CheckOutlined />}
                    aria-label={t("confirm_batch_write_off_action")}
                    disabled={!canWriteOff}
                    loading={loading.includes(`confirm-${record.id}`)}
                    onClick={() => openActionModal("confirm", record)}
                  />
                </span>
              </Tooltip>
            )}
            {record.canReverse && (
              <Tooltip
                title={
                  canWriteOff
                    ? t("restore_write_off_quantity_action")
                    : t("not_allowed")
                }
              >
                <span>
                  <Button
                    danger
                    size="small"
                    shape="circle"
                    icon={<RollbackOutlined />}
                    aria-label={t("restore_write_off_quantity_action")}
                    disabled={!canWriteOff}
                    loading={loading.includes(`reverse-${record.id}`)}
                    onClick={() => openActionModal("reverse", record)}
                  />
                </span>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("wms_pending_batch_write_offs")}
        subTitle={t("wms_pending_batch_write_offs_management")}
        extra={[
          <RefreshBtn
            key="refresh"
            loading={loading.includes("list")}
            onClick={getData}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Divider key="divider" type="vertical" />,
          <Button
            key="reset"
            danger
            icon={<ClearOutlined />}
            onClick={resetFilters}
            loading={loading.includes("list")}
          >
            {t("reset")}
          </Button>,
        ]}
      />

      <Alert
        className={styles.notice}
        type="warning"
        showIcon
        message={t("batch_write_off_pending_notice_title")}
        description={t("batch_write_off_pending_notice_description")}
      />

      <SearchFilterCard
        formId="batch-write-off-filter"
        searchValue={undefined}
        setSearchValue={() => undefined}
        onReset={resetFilters}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
        visiableSearch
      >
        <Form<IBatchWriteOffQuery>
          formId="batch-write-off-filter"
          defaultValues={query}
          onSubmit={submitFilters}
        >
          <BatchWriteOffFilterFields
            products={products}
            stores={stores}
            loading={loading}
            getProducts={getProducts}
          />
        </Form>
      </SearchFilterCard>

      <Table<IBatchWriteOff>
        rowKey="id"
        showPagination
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list === undefined}
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        scroll={{ x: 2580 }}
        onPaginationChange={(page, perPage) => {
          setQuery((current) => ({
            ...current,
            page,
            perPage,
          }));
        }}
      />

      <Modal
        title={
          actionModal?.type === "confirm"
            ? t("confirm_batch_write_off")
            : t("reverse_batch_write_off")
        }
        open={Boolean(actionModal)}
        onCancel={() => {
          setActionModal(undefined);
          setActionNotes("");
        }}
        onOk={submitAction}
        okText={
          actionModal?.type === "confirm"
            ? t("confirm_batch_write_off_action")
            : t("restore_write_off_quantity_action")
        }
        cancelText={t("cancel")}
        confirmLoading={
          actionModal
            ? loading.includes(`${actionModal.type}-${actionModal.record.id}`)
            : false
        }
        okButtonProps={{ danger: actionModal?.type === "reverse" }}
        width={560}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type={actionModal?.type === "confirm" ? "info" : "warning"}
            showIcon
            message={
              actionModal?.type === "confirm"
                ? t("confirm_batch_write_off_notice")
                : t("reverse_batch_write_off_notice")
            }
          />
          <Input.TextArea
            rows={4}
            maxLength={1000}
            showCount
            value={actionNotes}
            placeholder={t("notes")}
            onChange={(event) => setActionNotes(event.target.value)}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default BatchWriteOffPage;
